import LargeForm from 'components/forms/LargeForm'
import SectionTitle from 'components/global/SectionTitle'
import { Col, Row, Section } from 'components/grid'
import Accelerator from 'components/intervention/Accelerator'
import ENILStarterKits from 'components/intervention/ENILStarterKits'
import IRLAStarterKits from 'components/intervention/IRLAStarterKits'
import ToolkitTabs from 'components/intervention/ToolkitTabs'
import Layout from 'components/Layout'
import OverlayVideo from 'components/overlay-video'
import SubNav from 'components/SubNav'
import { H1 } from 'components/typography/Headings'
import { P } from 'components/typography/Paragraph'
import { graphql, useStaticQuery } from 'gatsby'
import { IGatsbyImageData, StaticImage } from 'gatsby-plugin-image'
import React from 'react'
import { Carousel } from 'react-responsive-carousel'

export default function ToolkitsPage() {
	const data = useStaticQuery<ToolkitQuery>(query)

	const { fstk, secondary, irla, enil, wideReading, toolkits, wideReadingElementary, toolkitsElementary } = data

	const irlaMinusBkWt = { edges: irla.edges.filter((e) => e.node.colorLevel === 'Pu' || e.node.colorLevel === 'Or') }

	const subNavItems = [
		{ name: 'Elementary', link: 'elementary' },
		{ name: 'Secondary', link: 'secondary' }
	]

	return (
		<Layout pageName="Toolkits">
			<SubNav items={subNavItems} />
			<Section flatGray>
				<Row className="items-center py-20">
					<Col width="w-full lg:w-7/12" className="pl-4 lg:pl-12">
						<H1 className="mb-3">Intervention Aligned to the Science of Reading</H1>
						<P>
							Whether your school uses ARC Core<sup>®</sup> or another high-quality ELA program, there are always students who need additional support to become
							successful readers. ARC provides multiple intervention solutions for different grade levels that provide the perfect blend of direct instruction and
							differentiated support. These programs can be used in class during the intervention block, after school, and in summer tutoring.
						</P>
					</Col>
					<Col width="w-full md:w-5/12" className="hidden md:block">
						<StaticImage src="../../static/images/intervention/page-hero.png" placeholder="blurred" alt="kids reading" />
					</Col>
				</Row>
			</Section>
			<Accelerator level="elementary" wideReading={wideReadingElementary} toolkits={toolkitsElementary} />
			<Section id="watch-it-in-action-elementary" margin="mt-20 sm:mt-40">
				<SectionTitle title="Learn More" />
				<Row className="items-center">
					<Col width="w-full md:w-3/4" className="mx-auto">
						<OverlayVideo title="ARC Accelerator for Elementary Grades" />
					</Col>
				</Row>
			</Section>
			<Section margin="mt-32" id="fstk" scrollMarginTop="75px">
				<SectionTitle
					title="IRLA Foundational Skills Toolkits"
					text="IRLA Foundational Skills Toolkits are designed for high-dosage, differentiated small-group tutoring and intervention by
the classroom teacher and/or other educators. Explicit skills instruction includes phonological and phonemic awareness, phonics, encoding/decoding, word study, fluency, and spelling."
				/>
				<Row>
					<Col>
						<Carousel showThumbs={false} dynamicHeight showStatus={false} infiniteLoop showIndicators>
							<div>
								<StaticImage src="../../static/images/intervention/scope-and-sequence.png" alt="scope and sequence" />
							</div>
							<div>
								<StaticImage src="../../static/images/intervention/student-materials.png" alt="student materials" />
							</div>
							<div>
								<StaticImage src="../../static/images/intervention/teacher-materials.png" alt="teacher materials" />
							</div>
						</Carousel>
					</Col>
				</Row>
				<Row>
					<Col>
						<ToolkitTabs toolkits={fstk} />
					</Col>
				</Row>
			</Section>
			<Section id="enil-toolkits" margin="mt-32" scrollMarginTop="75px">
				<SectionTitle
					title="ENIL Toolkits (Herramientas ENIL)"
					text={[
						<span key={Math.random()} className="mb-3 block">
							The ENIL<sup>®</sup> Foundational Skills Toolkits (Herramientas para el desarrollo de Destrezas fundamentales, Amarillo–2 Rojo) are designed for
							high-dosage, differentiated small-group tutoring and intervention by the classroom teacher and/or other educators. Explicit Spanish-language skills
							instruction includes phonological and phonemic awareness, phonics, encoding/decoding, word study, fluency, and spelling.
						</span>,
						<span key={Math.random()}>The Blanco Toolkit helps teachers deliver targeted intervention in key literacy skills to intermediate grade students.</span>
					]}
				/>
				<Row>
					<Col>
						<ToolkitTabs toolkits={enil} />
					</Col>
				</Row>
			</Section>
			<Section margin="mt-32" id="enil-starter-kits" scrollMarginTop="75px">
				<ENILStarterKits />
			</Section>
			<Section margin="mt-32" id="irla-toolkits" scrollMarginTop="75px">
				<SectionTitle title="IRLA Toolkits" text="IRLA Toolkits help teachers deliver targeted intervention in key literacy skills to intermediate grade students." />
				<Row>
					<Col>
						<ToolkitTabs toolkits={irla} />
					</Col>
				</Row>
			</Section>
			<Section margin="mt-32" id="irla-starter-kits" scrollMarginTop="75px">
				<IRLAStarterKits />
			</Section>
			<Accelerator level="secondary" wideReading={wideReading} toolkits={toolkits} />
			<Section id="watch-it-in-action-secondary" margin="mt-20 sm:mt-40">
				<SectionTitle title="Learn More" />
				<Row className="items-center">
					<Col width="w-full md:w-3/4" className="mx-auto">
						<OverlayVideo title="ARC Accelerator for Secondary Grades" />
					</Col>
				</Row>
			</Section>
			<Section margin="mt-32" id="irla-toolkits-under-secondary" scrollMarginTop="75px">
				<SectionTitle title="IRLA Toolkits" text="IRLA Toolkits help teachers deliver targeted intervention in key literacy skills to intermediate grade students." />
				<Row>
					<Col>
						<ToolkitTabs toolkits={irlaMinusBkWt} />
					</Col>
				</Row>
			</Section>
			<Section margin="mt-32" id="secondary-toolkits" scrollMarginTop="75px">
				<SectionTitle
					title="IRLA Toolkits for Secondary Grades"
					text="Conceptually linked lessons build knowledge and reading skills. Age-appropriate, high-interest texts engage students in meaningful practice that builds confidence."
				/>
				<Row>
					<Col>
						<ToolkitTabs toolkits={secondary} />
					</Col>
				</Row>
			</Section>

			<Section margin="mt-32">
				<Row center className="pb-6">
					<Col width="lg:w-1/2">
						<LargeForm pageName="Intervention" />
					</Col>
				</Row>
			</Section>
		</Layout>
	)
}

export function extractColorLevel(value: string, first: boolean) {
	for (let key in toolkitProperties) {
		if (first) {
			if (value.substring(0, 2) === key) {
				return key
			}
		} else {
			if (value.includes(key)) {
				return key
			}
		}
	}
	return null
}

export interface ToolkitImage {
	id: string
	gatsbyImageData: IGatsbyImageData
}

export interface Toolkit {
	id: string
	colorLevel: keyof typeof toolkitProperties
	type: 'Foundational Skills' | 'IRLA' | 'Secondary' | 'ENIL'
	standard: 'CCSS' | 'TEKS'
	handbook: {
		title: string
		cover: ToolkitImage
	}
	scopeSequence: {
		title: string
		cover: ToolkitImage
	}
	chapterBooks?: ToolkitImage[]
	wordJournals?: ToolkitImage[]
	seriesBooks?: ToolkitImage[]
	bananagramasEnEspanol?: ToolkitImage[]
	genreCards?: ToolkitImage[]
	graphemeCards?: ToolkitImage[]
	smallGroupTexts?: ToolkitImage[]
	consonantPracticeCards?: ToolkitImage[]
	alphabetStrips?: ToolkitImage[]
	keyWordStrip?: ToolkitImage[]
	blendsAndDigraphsPracticeCards?: ToolkitImage[]
	powerWordKits?: ToolkitImage[]
	powerWordsRaceTrackGameSets?: ToolkitImage[]
	openSyllablesFlipBookletSet?: ToolkitImage[]
	keyWordFriezeBookmark?: ToolkitImage[]
	seriesauthorStudyTexts?: ToolkitImage[]
	soundSortingPictureCards?: ToolkitImage[]
	openSyllablesGameSet?: ToolkitImage[]
}

export interface ToolkitQueryNode {
	node: Toolkit
}

export interface ToolkitQueryEdge {
	edges: ToolkitQueryNode[]
}

export interface AcceleratorBasketQueryEdge {
	edges: { node: { title: string; gatsbyImageData: IGatsbyImageData } }[]
}

export interface AcceleratorToolkitQueryEdge {
	edges: { node: { name: string; images: { gatsbyImageData: IGatsbyImageData }[] } }[]
}

interface ToolkitQuery {
	fstk: ToolkitQueryEdge
	secondary: ToolkitQueryEdge
	irla: ToolkitQueryEdge
	enil: ToolkitQueryEdge
	wideReadingElementary: AcceleratorBasketQueryEdge
	toolkitsElementary: AcceleratorToolkitQueryEdge
	wideReading: AcceleratorBasketQueryEdge
	toolkits: AcceleratorToolkitQueryEdge
}

export const toolkitProperties = {
	Y: {
		order: 1,
		color: 'bg-core-y',
		name: 'Yellow',
		spanish_name: ''
	},
	A: {
		order: 1,
		color: 'bg-core-y',
		name: 'Amarillo',
		spanish_name: ''
	},
	'1G': {
		order: 2,
		color: 'bg-core-g',
		name: '1 Green',
		spanish_name: ''
	},
	V: {
		order: 2,
		color: 'bg-core-g',
		name: 'Verde',
		spanish_name: ''
	},
	'2G': {
		order: 3,
		color: 'bg-core-g',
		name: '2 Green',
		spanish_name: ''
	},
	T: {
		order: 3,
		color: 'bg-core-t',
		name: 'Turquesa',
		spanish_name: ''
	},
	'1B': {
		order: 4,
		color: 'bg-core-b',
		name: '1 Blue',
		spanish_name: ''
	},
	'1Az': {
		order: 4,
		color: 'bg-core-b',
		name: '1 Azul',
		spanish_name: ''
	},
	'2B': {
		order: 5,
		color: 'bg-core-b',
		name: '2 Blue',
		spanish_name: ''
	},
	'2Az': {
		order: 5,
		color: 'bg-core-b',
		name: '2 Azul',
		spanish_name: ''
	},
	'1R': {
		order: 6,
		color: 'bg-core-r',
		name: '1 Red',
		spanish_name: '1 Rojo'
	},
	'2R': {
		order: 7,
		color: 'bg-core-r',
		name: '2 Red',
		spanish_name: '2 Rojo'
	},
	Wt: {
		order: 8,
		color: 'bg-core-wt',
		name: 'White',
		spanish_name: ''
	},
	Bl: {
		order: 8,
		color: 'bg-core-wt',
		name: 'Blanco',
		spanish_name: ''
	},
	Bk: {
		order: 9,
		color: 'bg-black',
		name: 'Black',
		spanish_name: ''
	},
	Ne: {
		order: 9,
		color: 'bg-black',
		name: 'Negro',
		spanish_name: ''
	},
	Or: {
		order: 10,
		color: 'bg-core-or',
		name: 'Orange',
		spanish_name: ''
	},
	An: {
		order: 10,
		color: 'bg-core-or',
		name: 'Naranja',
		spanish_name: ''
	},
	Pu: {
		order: 11,
		color: 'bg-core-pu',
		name: 'Purple',
		spanish_name: ''
	},
	'1Br': {
		order: 12,
		color: 'bg-core-br',
		name: '1 Bronze',
		spanish_name: ''
	},
	'2Br': {
		order: 13,
		color: 'bg-core-br',
		name: '2 Bronze',
		spanish_name: ''
	},
	Si: {
		order: 13,
		color: 'bg-core-si',
		name: 'Silver',
		spanish_name: ''
	},
	Pl: {
		order: 13,
		color: 'bg-core-si',
		name: 'Plata',
		spanish_name: ''
	},
	Gl: {
		order: 14,
		color: 'bg-core-gl',
		name: 'Gold',
		spanish_name: ''
	}
}

const query = graphql`
	query {
		fstk: allContentfulToolkit(filter: { type: { eq: "Foundational Skills" } }) {
			edges {
				node {
					...toolkit
				}
			}
		}
		secondary: allContentfulToolkit(filter: { type: { eq: "Secondary" } }) {
			edges {
				node {
					...toolkit
				}
			}
		}
		irla: allContentfulToolkit(filter: { type: { eq: "IRLA" } }) {
			edges {
				node {
					...toolkit
				}
			}
		}
		enil: allContentfulToolkit(filter: { type: { eq: "ENIL" } }) {
			edges {
				node {
					...toolkit
				}
			}
		}
		wideReadingElementary: allContentfulAsset(
			filter: {
				title: {
					in: [
						"Wide Reading | 1G Basket 1"
						"Wide Reading | 2G Basket 1"
						"Wide Reading | 1B Basket 1"
						"Wide Reading | 2B Basket 1"
						"Wide Reading | 1R Basket 1"
						"Wide Reading | 2R Basket 1"
						"Wide Reading | Wt Basket 1"
						"Wide Reading | Bk Basket 1"
					]
				}
			}
		) {
			edges {
				node {
					title
					gatsbyImageData
				}
			}
		}
		toolkitsElementary: allContentfulProduct(
			filter: {
				name: {
					in: [
						"IRLA Foundational Skills Toolkit 1G"
						"IRLA Foundational Skills Toolkit 2G"
						"IRLA Foundational Skills Toolkit 1B"
						"IRLA Foundational Skills Toolkit 2B"
						"IRLA Foundational Skills Toolkit 1R"
						"IRLA Foundational Skills Toolkit 2R"
						"IRLA Foundational Skills Toolkit 2R"
						"IRLA Toolkit Wt"
						"IRLA Toolkit Bk"
					]
				}
			}
		) {
			edges {
				node {
					name
					images {
						gatsbyImageData
					}
				}
			}
		}
		wideReading: allContentfulAsset(
			filter: {
				title: {
					in: [
						"1R ARC Accelerator | Secondary Hook Book Basket"
						"2R ARC Accelerator | Secondary Hook Book Basket"
						"Wt ARC Accelerator | Secondary Hook Book Basket"
						"Bk ARC Accelerator | Secondary Hook Book Basket"
						"Or ARC Accelerator | Secondary Hook Book Basket"
						"Pu ARC Accelerator | Secondary Hook Book Basket"
					]
				}
			}
		) {
			edges {
				node {
					title
					gatsbyImageData
				}
			}
		}
		toolkits: allContentfulProduct(
			filter: {
				name: {
					in: [
						"IRLA Toolkit for Secondary Grades 1R"
						"IRLA Toolkit for Secondary Grades 2R"
						"IRLA Toolkit for Secondary Grades Wt"
						"IRLA Toolkit for Secondary Grades Bk"
						"IRLA Toolkit Or"
						"IRLA Toolkit Pu"
					]
				}
			}
		) {
			edges {
				node {
					name
					images {
						gatsbyImageData
					}
				}
			}
		}
	}
`
